<template>
    <div class="container container--devis">  

        <Loader v-if="!ready"></Loader>

        <article class="devis" v-if="ready">

            <header class="devis__heading">
                <h1 class="woocommerce-order-details__title">Devis {{ devis.numDisplayed }}</h1>
                <div class="devis__heading__date">
                    <p v-if="devis.isReady">
                        <strong>Émis le <Date :date="devis.createdAt" /></strong>
                        <strong v-if="devis.endAt">, valide jusqu'au <Date :date="devis.endAt" /></strong>
                    </p>
                    <p v-if="!devis.isReady">
                        <Badge type="warning">Traitement en cours</Badge>
                    </p>
                </div>
                <div class="devis__heading__actions">
                    <Button class="woocommerce-button button"
                        v-if="devis.isReady"
                        :disabled="devis.isRetrieveLocked"
                        :loading="processingRetrieve"
                        @click.native="retrieve()">         
                        Demander PDF
                        <i class="fa-solid fa-down-to-line"></i>
                    </Button>                    
                    <Button class="woocommerce-button button alt"
                        :disabled="downloading || devis.nbDocuments < 1"
                        :loading="!!downloading"
                        v-if="devis.isReady"
                        @click.native="download()">
                        Télécharger
                        <i class="fa-solid fa-down-to-line"></i>
                    </Button>                    
                    <button class="woocommerce-button button alt"
                        v-if="devis.isPurchasable && $can('COMMANDER')"
                        @click="purchase()">Commander</button>
                </div>
            </header>         
            
            <div class="devis__details">
                <ul class="woocommerce-order-overview woocommerce-thankyou-order-details order_details">
                    <li class="woocommerce-order-overview__order order">Type : <strong>{{ devis.devisTypeLibelle }}</strong></li>
                    <li class="woocommerce-order-overview__order order">Utilisateur : 
                        <strong v-if="devis.utilisateur">{{ devis.utilisateur.email }}</strong>
                        <strong v-else>—</strong>
                    </li>
                    <li class="woocommerce-order-overview__order order">Référence client : <strong>{{ devis.engagementNum }}</strong></li>
                    <li class="woocommerce-order-overview__order order">Code client : <strong>{{ devis.clientCode }}</strong></li>
                    <li class="woocommerce-order-overview__order order" v-if="devis.budgetCode">Code budget : <strong>{{ devis.budgetCode }}</strong></li>
                </ul>
            </div>

            <div class="woocommerce-order-details">
                <Affaire :data="devis.affaire" />               
                <strong v-if="devis.vendeur">Suivie par {{ devis.vendeur.name }}<br /></strong>                
                <Livraison 
                    :code="devis.deliveryMode" 
                    :scheduleInfos="devis.deliveryScheduleInfos" 
                    :infos="devis.deliveryInfos"
                    :indicationsFDV="devis.indicationsFDV" 
                />                 
            </div>

            <div class="woocommerce-order-details">
                <h3>Adresse de livraison</h3>
                <Address :data="devis.deliveryAddress" />              
            </div>   

            <div class="devis__articles">

                <List class="list--table">                    

                    <template v-slot:checkboxActions>

                        <button class="woocommerce-button button alt"
                            :disabled="processing || !itemSelected || unpurchasableSelected"
                            v-on:click="buy()">
                            <span class="fa fa-shopping-cart"></span>
                                Ajouter au panier
                        </button>

                        <Button class="alt"
                            :disabled="!itemSelected"
                            :loading="processingDocuments"
                            @click.native="downloadDocs()">         
                            Télécharger les documentations
                            <i class="fa-solid fa-down-to-line"></i>
                        </Button>

                    </template>

                    <template v-slot:heading>                
                        <div class="cell cell--cb">
                            <TogglerAll />
                        </div>
                        <div class="cell cell--ref">Réf.</div>
                        <div class="cell cell--secondary">UV.</div>
                        <div class="cell cell--secondary">Coef. UV</div>
                        <div class="cell cell--main">Libellé</div>
                        <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price">Tarif brut</div>
                        <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">% Remise</div>
                        <div class="cell cell--secondary">PU</div>
                        <div class="cell">Qté</div>
                        <div class="cell cell--price">Total HTT</div>
                        <div class="cell cell--secondary">TGAP</div>
                        <div class="cell cell--secondary">TVA</div>       
                    </template>

                    <template v-slot:footer>                
                        <div class="list__footer__row">
                            <div class="cell">Frais de livraison HT</div>
                            <div class="cell"><Price :amount="devis.deliveryCost" :currency="devis.currency" /></div>
                            <div></div>
                        </div>
                        <div v-if="devis.ftc && devis.ftc > 0" class="list__footer__row">
                            <div class="cell">                            
                                {{$setting('FTC_LABEL')}} HT
                            </div>

                            <div class="cell"><Price :amount="devis.ftc" :currency="devis.currency" /></div>
                            <div></div>
                        </div>                          
                        <div class="list__footer__row">
                            <div class="cell">Total HT</div>
                            <div class="cell"><Price :amount="devis.amountHT" :currency="devis.currency" /></div>
                            <div></div>
                        </div>
                        <div class="list__footer__row">
                            <div class="cell">Total TTC</div>
                            <div class="cell"><Price :amount="devis.amountTTC" :currency="devis.currency" /></div>
                        </div>
                        <div class="list__footer__row" v-if="devis.isPurchasable && $can('COMMANDER')">
                            <div class="cell"></div>
                            <div class="cell">
                                <a class="button alt" title="Passer commande"
                                    v-on:click="purchase()">Commander</a>
                            </div>
                        </div>
                    </template>

                    <ul class="list__content">

                        <li class="list__content__item"
                            v-for="ligne in devis.devisLignes"
                            :key="ligne.id">

                            <ArticleItem
                                :data="ligne">

                                <div class="cell cell--cb">
                                    <input type="checkbox"
                                        :disabled="!ligne.articleSlug"
                                        :value="ligne"
                                        v-model="selectedItems"
                                        @click.capture="$event.stopPropagation()" />
                                </div>
                                <div class="cell cell--ref">{{ ligne.articleReference }}</div>
                                <div class="cell cell--secondary">{{ ligne.uvCodeLibelle }}</div>
                                <div class="cell cell--secondary">x{{ ligne.uvCoef }}</div>
                                <div class="cell cell--main">{{ ligne.libelle }}</div>
                                <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price"><Price :amount="priceRawHTT(ligne)" :currency="devis.currency" /></div>
                                <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">{{ remiseClientPct(ligne) }}</div>
                                <div class="cell cell--secondary"><Price :amount="ligne.priceHTT" :currency="devis.currency" /></div>
                                <div class="cell cell--qty">{{ ligne.qty }}</div>
                                <div class="cell cell--price"><Price :amount="ligne.amountHTT" :currency="devis.currency" /></div>
                                <div class="cell cell--secondary"><Price :amount="ligne.tgap" :currency="devis.currency" /></div>
                                <div class="cell cell--secondary"><Tax :rate="ligne.tva" /></div>

                            </ArticleItem>

                        </li>
                        
                    </ul>

                </List>

            </div>

        </article>

    </div>
</template>

<script>

import { mapState } from 'vuex'
import { goTo } from '@/router'
import api from '@/api/proxyWeb/devis'
import cart from '@/containers/cart.vue'
import title from "@/containers/title"
import Address from '@/components/address/Address.vue'
import ArticleItem from '@/components/articleItem/ArticleItem.vue'
import Badge from '@/components/badge/Badge.vue'
import Button from '@/components/button/Button.vue'
import Date from '@/components/date/Date.vue'
import List, { TogglerAll }  from '@/components/list/List.vue'
import Livraison from '@/components/livraison/Livraison.vue'
import Price from '@/components/price/Price.vue'
import Loader from '@/components/loader/Loader.vue'
import Tax from '@/components/tax/Tax.vue'
import Affaire from '@/components/affaire/Affaire.vue'
import selectAll from '@/containers/selectAll.vue'

export default {

    name: 'Devis',

    data() {
        return {
            downloading: false,
            processingDocuments: false,
            processingRetrieve: false,
            devis: null,
            ready: false       
        }
    },

    computed: {
        
        ...mapState({
            currentClient: state => state.clients.current
        }),

        unpurchasableSelected: function() {
            return this.selectedItems.some( commandeLigne => !commandeLigne.isPurchasable )
        }
    },

    methods: {

        buy() {

            let articles = this.selectedItems.map(devisLigne => {
                    return {
                        'id': devisLigne.articleId,
                        'quantity': devisLigne.qty,
                        'variationId': devisLigne.articleVariationId
                    }
                })

            this.addToCart(articles);
        },
            
        cancelAutoUpdate () {
            clearInterval(this.timer);
        },  

        displayPdfRequestInfo () {
            this.$notify.success( 
                'Une version plus récente du fichier PDF sera bientôt mise à votre disposition via le bouton "Télécharger".<br/>Nous vous invitons à revenir dans quelques instants pour télécharger votre fichier.',
                {
                position: "bottom-right",
                duration : 20000,
                type: 'info',
                containerClass: 'toasted-outer',
                className: 'toasted-inner',
                action: {
                    text: 'OK',
                    onClick:(e, toast) => {
                        toast.goAway(0);
                    }
                }  
            })
        },

        download() {

            if ( !! this.downloading || this.devis.nbDocuments < 1 ) return

            this.downloading = true;
            api.getFile(this.devis.id)
                .then(() => this.$notify.success('Téléchargement en cours...') )
                .catch(() => this.$error( 'Impossible de télécharger les documents' ))
                .finally(() => { this.downloading = false } ) 
        },

        downloadDocs() {
            this.processingDocuments = true
            api.getDocuments(this.devis.id, this.selectedItems.map( devisLigne => devisLigne.num ))
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingDocuments = false } )
        },

        priceRawHTT: function(ligne) {
            return ( ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.priceRawHTT
        },

        purchase: function() {
            goTo( this.$config.SITE_URL + '/wp-content/plugins/myAdelya/tasks/purchase/devis/' + this.devis.id )
        },

        refreshDevis () {

            api.get(this.devis.id).then( (content) => 
            {
                this.devis = content
                if ( !this.devis.isRetrieveLocked ) {
                    this.processingRetrieve = false
                    this.cancelAutoUpdate()
                }
            })

        },

        remiseClientPct: function(ligne) {
            return ( ligne.remiseClient == null || ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.remiseClient * 100 + '%'
        },

        retrieve() {
            this.processingRetrieve = true
            api.retrieve(this.devis.id)
                .then( content => { 
                    this.devis = content                  
                    this.timer = setInterval(this.refreshDevis, 30000);
                    this.displayPdfRequestInfo()
                })
        }        
    },
  
    created () {
            let promises = [ api.get(this.$route.params.id), api.get(this.$route.params.id + this.$route.meta.tablePath) ]

            Promise
                .all(promises)
                .then( (content) => {
                    this.devis = content[0]
                    this.table = content[1]
                    this.ready = true
                    this.setTitle( 'Devis ' + this.devis.numDisplayed ) 
                    this.processingRetrieve = this.devis.isRetrieveLocked
                    if (this.devis.isRetrieveLocked) {
                        this.timer = setInterval(this.refreshDevis, 30000);
                        this.displayPdfRequestInfo()
                    }
                } )
                .catch(error => {
                    this.$errorPage(error.reason)
                })

    },

    beforeDestroy () {
      this.cancelAutoUpdate()
    },

    watch:{

        devis: function() {            

            this.selectableItems = []

            if ( !this.devis ) {
                return
            }
            
            this.devis.devisLignes.forEach(ligne => {
                if ( !ligne.articleSlug ) return
                this.selectableItems.push(ligne)
            })
        }
    },

    components: {
        Address,
        ArticleItem,
        Badge,
        Button,
        Date,
        List,
        Loader,
        Livraison,
        Price,
        Tax,
        TogglerAll,
        Affaire
    },

    mixins: [ selectAll, title, cart ]
}
</script>

<style>

.devis {
    position: relative;
}

.devis__heading {
    margin-bottom: 2rem;
    padding-top: 4rem; 
}

.devis__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
}

.devis__heading__actions > *:not(:first-child) {
    margin-left: 1rem !important;
}

.devis__footer {
}

.devis__footer__actions {
    text-align: right;
}

.toasted-outer .toasted.toasted-inner {
  font-size: 16px;
  border-radius: 5px;
  font-family: "Open Sans",sans-serif;
}

.toasted .primary .action, .toasted.toasted-primary .action {
    color: #274880 !important;
}

</style>
